import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { ArrowUpIcon, LoaderCircleIcon } from 'lucide-react';
import { askCarli, AgentMessage } from '../utils/carliAgent';
type ChatMessage = {
  id: number;
  role: 'assistant' | 'user';
  text: string;
};
const starterPrompts = ['What inventory should I send first?', 'How does recovery pricing work?', 'Which channels fit my brand?'];
const GREETING = 'Hi, I’m Carli. Ask me about recovery strategy, inventory, or the best place to start.';
const DEFAULT_CONTEXT = 'The visitor is on the Manymoons marketing site and has not connected inventory yet. Help them understand how recovery works and what to send first. Keep answers to two short paragraphs at most.';
export function CarliChat({
  prompts = starterPrompts,
  context = DEFAULT_CONTEXT,
  subheading = 'Your recovery intelligence layer'
}: {
  prompts?: string[];
  context?: string;
  subheading?: string;
} = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: 1,
    role: 'assistant',
    text: GREETING
  }]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, thinking]);
  const sendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const question = draft.trim();
    if (!question || thinking) return;
    const history: AgentMessage[] = [...messages.filter(message => message.text !== GREETING).map(message => ({
      role: message.role === 'assistant' ? 'assistant' as const : 'user' as const,
      content: message.text
    })), {
      role: 'user',
      content: question
    }];
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'user',
      text: question
    }]);
    setDraft('');
    setError(null);
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context,
        maxTokens: 400
      });
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: reply
      }]);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setThinking(false);
    }
  };
  return <aside className="flex flex-col bg-[#BB5E40] p-6 text-cream sm:p-8" aria-labelledby="carli-chat-heading" data-id="element-452" data-mp="192qh7mzi8m4a">
      <div className="flex items-center gap-2" data-id="element-453" data-mp="1e001ypby89ls">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-cream/15" data-id="element-454" data-mp="1izuuexbdh78y">
          <span aria-hidden="true" className="h-4 w-4 bg-cream" style={{
          WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/uszYpUgDHbmVmVvdioqNYy/Icon_OffWhite.svg)',
          maskImage: 'url(https://cdn.magicpatterns.com/uploads/uszYpUgDHbmVmVvdioqNYy/Icon_OffWhite.svg)',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain'
        }} data-id="element-455" data-mp="1j1pde6332pae" />
        </span>
        <div data-id="element-456" data-mp="3ci26j3qaiibi">
          <h2 id="carli-chat-heading" className="text-sm font-semibold" data-id="element-457" data-mp="3chtvzihn3p3h">Ask Carli</h2>
          <p className="text-xs text-cream/65" data-id="element-458" data-mp="37k0n2hoycgzi">{subheading}</p>
        </div>
      </div>

      <div ref={scrollRef} className="mt-5 h-36 shrink-0 space-y-3 overflow-y-auto pr-1" style={{
      scrollbarGutter: 'stable',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(253,251,247,0.35) transparent'
    }} aria-live="polite" data-id="element-459" data-mp="91mqr7jktjsl">
        {messages.map(message => <div key={message.id} className={`max-w-[92%] whitespace-pre-line rounded-2xl px-3.5 py-3 text-sm leading-relaxed ${message.role === 'assistant' ? 'rounded-tl-sm bg-cream/14 text-cream' : 'ml-auto rounded-tr-sm bg-cream text-ink'}`} data-id="element-460" data-mp="3r7rjrxbnqcs5">
            {message.text}
          </div>)}
        {thinking && <div className="flex items-center gap-2 text-xs text-cream/70" data-id="element-461" data-mp="cacwiyztwekl">
            <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-462" data-mp="ccfv47f1rg0x" />
            Carli is thinking…
          </div>}
        {error && <p className="text-xs text-cream/80" data-id="element-463" data-mp="2js11kfgbq5w">{error}</p>}
      </div>

      {messages.length === 1 && <div className="mt-4 flex shrink-0 flex-wrap gap-2" aria-label="Suggested questions" data-id="element-464" data-mp="qb5s7n1v9fpa">
          {prompts.map(prompt => <button key={prompt} type="button" onClick={() => setDraft(prompt)} className="rounded-full border border-cream/25 px-2.5 py-1.5 text-left text-[11px] font-medium text-cream/85 transition-colors hover:bg-cream/12" data-id="element-465" data-mp="v8fp1nrl4tn5">
              {prompt}
            </button>)}
        </div>}

      <form onSubmit={sendMessage} className="mt-5 flex shrink-0 items-center gap-2 rounded-xl bg-cream p-1.5" data-id="element-466" data-mp="362bl3znz7ppm">
        <label htmlFor="carli-message" className="sr-only" data-id="element-467" data-mp="2yg6944soge5s">Message Carli</label>
        <input id="carli-message" value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli anything…" className="min-w-0 flex-1 bg-transparent px-2 text-sm text-ink placeholder:text-ink/45 outline-none" data-id="element-468" data-mp="2thmoc7ptl8f0" />
        <button type="submit" disabled={!draft.trim() || thinking} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-forest text-cream transition-colors hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-40" aria-label="Send message" data-id="element-469" data-mp="2fqgebhowzsdv">
          {thinking ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-470" data-mp="2kp1ittb78y84" /> : <ArrowUpIcon className="h-4 w-4" data-id="element-471" data-mp="1wzys7vyl7v1h" />}
        </button>
      </form>
    </aside>;
}